import React, { useState, useEffect } from 'react';
import { Button, Space, Select, Popover, Typography } from '@bedrock/components';
import { Plus } from '@bedrock/icons-react';
import { get } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import dayjs from 'dayjs';
import { MPCJsonLogic, $sType, localPx2rem } from '#utils/index';
import { defs } from '#services';
import CooperationDetails from '#features/CooperationFormList/CooperationDetails';
import { getRequiredRule, getOptionsRule, getLogicOptions, getConditionDisabled } from '#features/utils';
import classNames from 'classnames/bind';
import { ProTable } from '@bedrock/eed-react';
import ClosingReport from '../ClosingReportTable/ClosingReport';
import styles from './index.less';


ProTable.addEditRender(
  'mpc-closingReport',
  ({ props, setRow, toggleEditableRowKey, getRowKey }, editable) => ({
    component: editable
      ? ClosingReport
      : (props) => (
          <div
            onClick={() => {
              toggleEditableRowKey?.(
                getRowKey?.(props?.row) as string,
                editable
              );
            }}
          >
            <ClosingReport {...props} view="view"/>
          </div>
        ),
    props: {
      ...props,
    },
  })
);


const cx = classNames.bind(styles);

const renderLabel = (option: any) => {
  return <div className={cx('render-label')}>
    {option.name}
  </div>
}

// 深层合并如果key一样且是对象，则合并对象，否则覆盖
const objectDeepMerge = (obj1: any, obj2: any): any => {
  // 如果任一参数不是对象类型,直接返回obj2
  if (!obj1 || !obj2 || typeof obj1 !== 'object' || typeof obj2 !== 'object') {
    return obj2;
  }
  // 使用Object.assign创建新对象,避免修改原对象
  const newObj = Object.assign({}, obj1);
  // 遍历obj2的所有属性
  Object.keys(obj2).forEach((key) => {
    // 如果obj1中不存在该属性,直接赋值
    if (!(key in obj1)) {
      newObj[key] = obj2[key];
      return;
    }
    // 如果两个对象都有该属性且都是对象类型,递归合并
    if (typeof obj1[key] === 'object' && typeof obj2[key] === 'object') {
      // 如果是数组,直接覆盖
      if (Array.isArray(obj1[key]) || Array.isArray(obj2[key])) {
        newObj[key] = obj2[key];
      } else {
        newObj[key] = objectDeepMerge(obj1[key], obj2[key]);
      }
    } else {
      // 否则用obj2的值覆盖
      newObj[key] = obj2[key];
    }
  });
  return newObj;
};

const mergeRenderFn = (...renderFnList: any[]) => {
  return (...args: any[]) => {
    let renderConfig = {};
    for (const renderFn of renderFnList) {
      renderConfig = objectDeepMerge(
        renderConfig,
        typeof renderFn === 'function' ? renderFn(...args) : renderFn
      );
    }
    return renderConfig;
  };
};

// 千分位格式化
const thousandthFormat = (value: number | string) => {
  if (value === undefined) return '';
  const [intPart, decimalPart] = `${value}`.split('.');
  // 判断是否有小数点
  const hasDecimal = value.toString().includes('.');
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return hasDecimal ? `${formattedInt}.${decimalPart}` : formattedInt;
};

// level层id
const levelKeys = [
  {
    level: 0,
    key: 'cooperateAccountId',
  },
  {
    level: 1,
    key: 'quoteKey',
  },
  {
    level: 2,
    key: 'l2$subDataId',
  },
];

const totalItemKey = ['travelCateringPrice', 'unitPrice', 'quantity1', 'quantity2'];
export const getTotalPrice = (row: any): number => {
  // 处理totalItemKey，如果存在转数字，如果不存在则不处理
  totalItemKey.forEach(key => {
    if(row[key]) {
      // 如果转数字为NaN，则返回0
      row[key] = +row[key] || 0;
    }
  });
  let total = 0;
  total +=
    (row.travelCateringPrice ?? 0) +
    (row.unitPrice ?? 0) * (row.quantity1 ?? 0) * (row.quantity2 ?? 1);
  // 四舍五入，保留两位小数
  return Math.round(total * 100) / 100 || 0;
}
// 添加校验规则
const hanleColumnRule = (
  header: defs.DemandQuoteTableColumnDto,
  editRender: any
) => {
  return ({ value, row, index }) => {
    const editConfig = editRender({ value, row, index });
    const conditionDisabled = getConditionDisabled(header, row);
    const newEditConfig = {
      ...editConfig,
      rule: [
        ...getRequiredRule(header, row),
        ...(editConfig?.rule || []),
      ],
    };
    if (conditionDisabled && newEditConfig?.props && !value?.toString?.()) {
      newEditConfig.props = {
        ...newEditConfig.props,
        disabled: true,
        key: 'disabled',
      };
    }
    return newEditConfig;
  };
};

// 是数组类型的code
const arrayCode = ['dropdown_list', 'date_range'];
export const createRowByCooperateAccountId = (
  headers: defs.DemandQuoteTableColumnDto[],
  cooperateAccountList: defs.DemandCooperateAccountDto[],
  cooperateAccountId: number
) => {
  let row = {
    cooperateAccountId,
    quoteKey: uuidv4(),
  };
  const cooperateAccount = cooperateAccountList?.find?.(
    (n) => n.id === cooperateAccountId
  );
  headers.forEach((header) => {
    // 匹配合作的属性值
    if (header.code.match(/^(standardCode|l1\$DCP)/)) {
      cooperateAccount?.property_values?.some?.((n) => {
        let propertyCode = header.code.replace('l1$', '');
        if (n.property_code === propertyCode) {
          const isArray = arrayCode.includes(header.data_type);
          row[header.code] = isArray ? n.value : n.value?.[0] || '';
          return true;
        }
      });
    }
  });
  return row;
};

  // 根据standards子节点形成展开的标品配置
  export const createPreFillValueWithStandardId = (columns:defs.DemandQuoteTableColumnDto[], cooperateAccountList: defs.DemandCooperateAccountDto[]) => {
    const endChildList = [];
    const standardIdList = columns?.reduce((acc, item) => {
      if(item.code?.includes('standardCode')) {
        acc.push(...item.standards);
      }
      return acc;
    }, []) || [];
    const standardIdMap = standardIdList?.reduce((acc, item) => {
      acc[item.code] = item;
      if(item.parent_code) {
        acc[item.parent_code].children = acc[item.parent_code].children || [];
        acc[item.parent_code].children.push(item);
      }
      return acc;
    }, {}) || {};

    function createItem(item: any, result: any = {}) {
      result[`standardCode${item.level}`] = item.code;
      if (item.parent_code) {
        createItem(standardIdMap[item.parent_code], result);
      }
      return result;
    }
    Object.values(standardIdMap).forEach((item: any) => {
      if(!item?.children?.length) {
        endChildList.push(createItem(item));
      }
    });
    let resultList = [];
    cooperateAccountList?.forEach(item => {
      if (endChildList.length) {
        resultList.push(...endChildList.map(n => {
          return {
            ...n,
            cooperateAccountId: item.id,
            quoteKey: uuidv4(),
            l2$subDataId: uuidv4(),
          }
        }));
      } else {
        resultList.push({
          ...item,
          cooperateAccountId: item.id,
          quoteKey: uuidv4(),
          l2$subDataId: uuidv4(),
        });
      }
    });
    // 判断resultList的quoteKey是否重复，如果重复则删除
    const quoteKeySet = new Set();
    resultList = resultList.filter(item => {
      if(quoteKeySet.has(item.quoteKey)) {
        return false;
      }
      quoteKeySet.add(item.quoteKey);
      return true;
    });
    return resultList;
  }

const defaultWidth = 200;

const headerHandleTemps = [
  {
    codePattern: /^cooperateAccountId/, // 合作内容
    level: 0,
    isEdit: false,
    handle: (
      header: defs.DemandQuoteTableColumnDto,
      cooperateAccountList: defs.DemandCooperateAccountDto[]
    ) => {
      return {
        ...header,
        isEdit: false,
        fixed: 'left',
        width: 110,
        render: (value, row, index) => {
          const cooperateAccount = cooperateAccountList?.find?.(
            (n) => n.id == value
          );
          return (
            <CooperationDetails
              key={cooperateAccount?.index_id}
              maxRows={1}
              showMoreButtonPosition="titleAfter"
              value={cooperateAccount?.property_values || []}
              index_id={cooperateAccount?.index_id || 1}
              showValuesTitle
            />
          );
        },
      };
    },
  },
  {
    codePattern: /^standardCode+/, // 标品
    level: 1,
    handle: (header: defs.DemandQuoteTableColumnDto) => {
      // 获取标品层级
      const sLevel = +header.code.replace(/^standardCode/, '');
      const parentCode = `standardCode${sLevel - 1}`;
      return {
        ...header,
        render: (value, row, index) => {
          const option = header.standards?.find((n) => n.code === value);
          if(!option) {
            return value || '--';
          }
          return option?.name || value || '--';
        },
        editRender({ value, row, index }) {
          let options: any[] =
            header.standards?.filter((n) => n.level === sLevel) || [];
          if (sLevel > 1) {
            options = options.filter(
              (n) => n.parent_code === row[parentCode]?.toString()
            );
          }
          options =
            options?.map?.((n) => {
              return {
                value: n.code,
                name: n.name,
                short_code: n.short_code,
                id: n.id,
              };
            }) || [];
          return {
            props: {
              options,
            },
          };
        },
      };
    },
  },
  {
    codePattern: /^l1\$DCP/, // 关联属性
    level: 1,
    handle: (header: defs.DemandQuoteTableColumnDto) => {
      return {
        ...header,
      };
    },
  },
  {
    codePattern: /^l1\$DQP/, // 其他属性
    level: 1,
    handle: (header: defs.DemandQuoteTableColumnDto) => {
      return {
        ...header,
      };
    },
  },
  {
    codePattern: /^l1\$/, // 二级子属性
    level: 1,
    handle: (header: defs.DemandQuoteTableColumnDto) => {
      return {
        ...header,
      };
    },
  },
  {
    codePattern: /^l2\$/, // 三级子属性
    level: 2,
    handle: (header: defs.DemandQuoteTableColumnDto) => {
      return {
        ...header,
      };
    },
  },
  {
    codePattern: /^unitPrice/, // 单价
    level: 1,
    handle: (header: defs.DemandQuoteTableColumnDto) => {
      return {
        ...header,
        render(v, row, index) {
          return <div style={{ textAlign: 'right' }}>
            {v ? thousandthFormat(Number(v)?.toFixed?.(2)) : '--'}
          </div>
        },
      };
    },
  },
  {
    codePattern: /^totalPrice$/, // 总价
    level: 1,
    isEdit: false,
    handle: (header: defs.DemandQuoteTableColumnDto) => {
      return {
        ...header,
        width: 120,
        fixed: 'right',
        render(v, row, index) {
          return <div className={cx('total-price')}>
            {thousandthFormat(Number(getTotalPrice(row))?.toFixed?.(2)) || '0.00'}
          </div>
        },
        editRender() {
          return {
            editable: false,
          };
        },
        batchEditRender: null,
      };
    },
  },
  {
    isDefault: true,
    codePattern: /^.*$/, // 默认属性
    level: 1,
    handle: (header: defs.DemandQuoteTableColumnDto) => {
      return {
        ...header,
      };
    },
  },
];

const CopyToMoreCooperateAccount = function (props: {
  cooperateObjList: any[];
  children: React.ReactNode;
  onConfirm: (value: number[]) => void;
}) {
  const { cooperateObjList, children, onConfirm } = props;
  const [visible, setVisible] = useState(false);
  const [selectValues, setSelectValues] = useState([]);
  const options = cooperateObjList.map((n, index) => ({
    name: `合作内容${n.index_id}`,
    value: n.id,
  }));

  useEffect(() => {
    setSelectValues([]);
  }, [visible]);

  const content = (
    <Space
      direction="vertical"
      style={{
        width: 300,
      }}
    >
      复制新增到
      <Select
        style={{
          width: '100%',
        }}
        multiple={true}
        options={options}
        value={selectValues}
        onChange={(value: string[]) => setSelectValues(value)}
      />
      <Space
        size={12}
        style={{
          width: '100%',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          onClick={() => {
            setVisible(false);
          }}
        >
          取消
        </Button>
        <Button
          type="primary"
          onClick={() => {
            onConfirm?.(selectValues);
            setVisible(false);
          }}
        >
          确定
        </Button>
      </Space>
    </Space>
  );
  return (
    <Popover
      placement="bottomLeft"
      title=""
      content={content}
      visible={visible}
      onVisibleChange={setVisible}
      trigger="click"
    >
      {children}
    </Popover>
  );
};

const headerTypeToColumnConfig = ({
  header,
  index,
  cooperateAccountList,
  quoteTableType,
  readOnly,
}: {
  quoteTableType: QuoteTableType;
  header: defs.DemandQuoteTableColumnDto;
  index: number;
  cooperateAccountList: defs.DemandCooperateAccountDto[];
  readOnly?: boolean;
}) => {
  const { data_type, code, label } = header;
  // headerHandleTemps按照顺序匹配到一个就返回
  const columnTemp = headerHandleTemps.find((n) => {
    if (n.codePattern.test(code)) {
      return n;
    }
  });
  const { level, isDefault, handle } = columnTemp;
  const tempHandleConfig = handle(header, cooperateAccountList);

  //   mergeRowSpanKey根据level层级从levelKey中获取，levelKeys中level为0的key为[cooperateAccountId]，level为1的key为[cooperateAccountId, quoteKey]
  const mergeRowSpanKey = levelKeys.reduce((acc, n) => {
    if (n.level <= level) {
      acc.push(n.key);
    }
    return acc;
  }, []);

  let config: any = {
    title: <span className={cx({
      'required-text': header.required_mode === 1 && !readOnly,
    })}>{label}</span>,
    dataIndex: code,
    mergeRowSpanKey,
    level,
    ...tempHandleConfig,
  };

  if (!config.dataIndex) {
    config.dataIndex = `$temp_${index}`;
  }

  let defaultEditAble = config.isEdit ?? true;
  if (defaultEditAble) {
    // 策划预填阶段
    if (quoteTableType === 'prefill') {
      // 如果是KOL，则不需要编辑
      if (header.prefill) {
        defaultEditAble = true;
      } else {
        defaultEditAble = false;
      }
    }
    if (quoteTableType === 'supplierQuote') {
      // 同时不配置（策划拆单预填和供应商可编辑），则供应商可编辑
      if (!header.outsource_modify && !header.prefill) {
        defaultEditAble = true;
      } else if (header.outsource_modify === false) {
        defaultEditAble = false;
      } else {
        defaultEditAble = true;
      }
      // 关联属性，则直接取outsource_modify的值
      if (header.group_type === 2) {
        defaultEditAble = !!header.outsource_modify;
      }

      if(code?.includes('standardCode')) {
        if (!config.use_in_design_standard) {
          defaultEditAble = true;
        }
      }
    }
  }

  const editable = defaultEditAble && !readOnly;

  // 根据data_type处理编辑渲染
  switch (data_type) {
    case 'text':
      config = {
        width: 400,
        ...config,
        editRender: mergeRenderFn(({ value, row, index }) => {
          return {
            name: 'mpc-textArea',
            editable: defaultEditAble,
            rule: [
              {
                validator: (rule, value, callback) => {
                  if (value && value.length > 500) {
                    callback('最多500字');
                  } else {
                    callback();
                  }
                },
              },
            ],
            props: {
              placeholder: '请输入',
              maxLength: 500,
            },
          };
        }, config.editRender || {}),
        batchEditRender: mergeRenderFn(
          {
            editable: defaultEditAble,
            name: 'input',
            props: {
              placeholder: '请输入',
              maxLength: 500,
            },
          },
          config.batchEditRender || {}
        ),
      };
      break;
    case 'dropdown_list':
      config = {
        width: 200,
        ...config,
        editRender: mergeRenderFn(({ value, row, index }) => {
          const options = getLogicOptions(header, row);
          return {
            name: 'select',
            editable: defaultEditAble,
            rule: [
              ...getOptionsRule(options),
            ],
            props: {
              allowClear: true,
              multiple: true,
              options: options,
              autoWidth: false,
              showSearch: true,
              style: {
                maxWidth: localPx2rem(400),
              },
              renderLabel,
              dropdownStyle: {
                minWidth: localPx2rem(280),
                maxWidth: localPx2rem(400),
              },
            },
          };
        }, config.editRender || {}),
        batchEditRender: mergeRenderFn(
          {
            editable: defaultEditAble,
            name: 'select',
            props: {
              allowClear: true,
              multiple: true,
              options: getLogicOptions(header, 'all'),
              autoWidth: false,
              showSearch: true,
              renderLabel,
              style: {
                maxWidth: localPx2rem(400),
              },
              dropdownStyle: {
                minWidth: localPx2rem(280),
                maxWidth: localPx2rem(400),
              },
            },
          },
          config.batchEditRender || {}
        ),
      };
      break;
    case 'dropdown_radio':
      config = {
        width: 200,
        ...config,
        editRender: mergeRenderFn(({ value, row, index }) => {
          const options = getLogicOptions(header, row);
          return {
            name: 'select',
            editable: defaultEditAble,
            rule: [
              ...getOptionsRule(options),
            ],
            props: {
              allowClear: true,
              options: options,
              autoWidth: true,
              showSearch: true,
              renderLabel,
              style: {
                maxWidth: localPx2rem(400),
              },
              dropdownStyle: {
                minWidth: localPx2rem(280),
                maxWidth: localPx2rem(400),
              },
            },
          };
        }, config.editRender || {}),
        batchEditRender: mergeRenderFn(
          {
            editable: defaultEditAble,
            name: 'select',
            props: {
              allowClear: true,
              options: getLogicOptions(header, 'all'),
              autoWidth: true,
              showSearch: true,
              renderLabel,
              style: {
                maxWidth: localPx2rem(400),
              },
              dropdownStyle: {
                minWidth: localPx2rem(280),
                maxWidth: localPx2rem(400),
              },
            },
          },
          config.batchEditRender || {}
        ),
      };
      break;
    case 'numeric':
      config = {
        width: 160,
        render(value, row, index) {
          return <div style={{ textAlign: 'right' }}>{thousandthFormat(value)}</div>;
        },
        ...config,
        editRender: mergeRenderFn(
          ({ value, row, index }) => ({
            name: 'inputNumber',
            editable: defaultEditAble,
            rule: [
              {
                validator: (rule, value, callback) => {
                  // 校验如果数据不为空，则必须是数字类型
                  if (!!value && isNaN(+value)) {
                    callback('请输入数字');
                  } else {
                    callback();
                  }
                },
              },
            ],
            props: {
              formatter: (value) => {
                return thousandthFormat(value);
              },
              parser: (value) => value.replace(/\s?|(,*)/g, ''),
              toFixed: 4,
              placeholder: '请输入',
              defaultValue: '',
              direction: 'vertical',
            },
          }),
          config.editRender || {}
        ),
        batchEditRender: mergeRenderFn(
          {
            editable: defaultEditAble,
            name: 'inputNumber',
            props: {
              toFixed: 4,
              placeholder: '请输入',
              direction: 'vertical',
            },
          },
          config.batchEditRender || {}
        ),
      };
      break;
    case 'date_input':
      config = {
        ...config,
        editRender: mergeRenderFn(
          ({ value, row, index }) => ({
            name: 'date',
            editable: defaultEditAble,
            rule: [
              {
                validator: (rule, value, callback) => {
                  // 校验如果数据不为空，则必须是日期类型
                  if (!!value && !dayjs(value).isValid()) {
                    callback('请输入日期');
                  } else {
                    callback();
                  }
                },
              },
            ],
            props: {
              placeholder: '请选择日期',
            },
          }),
          config.editRender || {}
        ),
        batchEditRender: mergeRenderFn(
          {
            editable: defaultEditAble,
            name: 'date',
            props: {
              placeholder: '请选择日期',
            },
          },
          config.batchEditRender || {}
        ),
      };
      break;
    case 'date_range':
      config = {
        width: 280,
        ...config,
        render(value, row, index) {
          return value?.join?.(' ~ ') || '--';
        },
        editRender: mergeRenderFn(
          ({ value, row, index }) => ({
            name: 'date',
            editable: defaultEditAble,
            rule: [
              {
                validator: (rule, value, callback) => {
                  // value是数组，两个日期类型
                  // 校验如果数据不为空，则必须是数字类型
                  if (
                    value?.some?.((n) => {
                      if (!!n && !dayjs(n).isValid()) {
                        return true;
                      }
                    })
                  ) {
                    callback('请输入日期');
                  } else {
                    callback();
                  }
                },
              },
            ],
            props: {
              range: true,
              placeholder: ['开始日期', '结束日期'],
            },
          }),
          config.editRender || {}
        ),
        batchEditRender: mergeRenderFn(
          {
            editable: defaultEditAble,
            name: 'date',
            props: {
              range: true,
              placeholder: ['开始日期', '结束日期'],
            },
          },
          config.batchEditRender || {}
        ),
      };
      break;
    case 'text_with_upload':
      config = {
        width: 380,
        ...config,
        editRender: mergeRenderFn(
          ({ value, row, index }) => ({
            name: 'text_with_upload',
            editable,
            props: {
              placeholder: '请选择日期',
              view: !editable,
            },
          }),
          config.editRender || {}
        ),
        batchEditRender: null
      };
      break;
    case 'input':
    default:
      config = {
        width: 200,
        ...config,
        editRender: mergeRenderFn(
          ({ value, row, index }) => ({
            name: 'input',
            editable: defaultEditAble,
            rule: [
              {
                validator: (rule, value, callback) => {
                  if (!!value && value.length > 50) {
                    callback('最多50字');
                  } else {
                    callback();
                  }
                },
              },
            ],
            props: {
              placeholder: '请输入',
              maxLength: 50,
            },
          }),
          config.editRender || {}
        ),
        batchEditRender: mergeRenderFn(
          {
            editable: defaultEditAble,
            name: 'input',
            props: {
              placeholder: '请输入',
              maxLength: 50,
            },
          },
          config.batchEditRender || {}
        ),
      };
  }

  if(!config.width) {
    config.width = defaultWidth;
  }

  if (data_type === 'text_with_upload' && readOnly) {
    return config;
  }

  if (!config.render) {
    config.render = (value, row, index) => {
      if (Array.isArray(value)) {
        return !value?.length ? '--' : value?.join(',');
      }
      return !value ? '--' : value;
    };
  }

  if (readOnly) {
    defaultEditAble = false;
    config.editRender = null;
    config.batchEditRender = null;
  }

  // 统一处理校验规则
  if (defaultEditAble) {
    config.editRender = hanleColumnRule(header, config.editRender);
  } else {
    config.batchEditRender = null;
    config.editRender = null;
  }
  // 动态配置选项不需要批量编辑
  // if (header.options?.groups?.length) {
  //   config.batchEditRender = null;
  // }

  // 不可批量编辑
  if (code.match(/^standardCode/) && code !== 'standardCode1') {
    config.batchEditRender = null;
  }

  if (['totalPrice'].includes(code)) {
    config.batchEditRender = null;
    config.editRender = null;
  }

  // 序号类型不可编辑
  if (code.endsWith('$index')) {
    config = Object.assign(config, {
      width: 50,
      render: (v, row, index) => {
        return v ? v + 1 : 1;
      },
      editRender: null,
      batchEditRender: null,
    });
  }
  return config;
};

export type QuoteTableType = 'prefill' | 'supplierQuote';

export function handleQuoteColumns({
  quoteTableType = 'supplierQuote',
  cooperateAccountList,
  headerColumns,
  tableRef,
  readOnly = false,
  addTemplateList,
  renderCheckboxCol,
}: {
  quoteTableType: QuoteTableType;
  needCooperateAccountId?: boolean; // 是否需要合作内容 列
  headerColumns: defs.DemandQuoteTableColumnDto[]; // 服务端定义的表头列
  cooperateAccountList: defs.DemandCooperateAccountDto[]; // 合作内容列表
  tableRef: any; // 表格实例
  readOnly?: boolean; // 是否只读
  addTemplateList?: any[]; // 添加模板列表,通过cooperateAccounId匹配
  renderCheckboxCol?: (params: any) => any;
}) {
  // 复制一份 headerColumns 以避免修改原数组
  const columns = [...headerColumns];

  // if (needCooperateAccountId) {
  //   columns?.unshift?.({
  //     code: 'cooperateAccountId',
  //     label: '合作内容',
  //     data_type: 'text',
  //   });
  // }

  // 将所有列转换为配置对象
  const columnsList = columns.map((h, index) =>
    headerTypeToColumnConfig({
      header: h,
      index,
      cooperateAccountList,
      quoteTableType,
      readOnly,
    })
  );
  // 获取所有不可以编辑的col
  const unEditableColumns = columnsList.filter((n) => !n.editRender);

  const createQuoteKeyTemplate = (cooperateAccountId: number) => {
    const match = addTemplateList?.find((n) => n.cooperateAccountId == cooperateAccountId);
    if (match) {
      return {
        ...match,
        quoteKey: uuidv4(),
      }
    }
    // 如果没有addTemplate，则通过cooperateAccountList匹配
    const cooperateAccount = cooperateAccountList?.find((n) => n.id == cooperateAccountId);
    if (cooperateAccount) {
      return createRowByCooperateAccountId(columns, cooperateAccountList, cooperateAccountId);
    }
    return {};
  }

  // 按照原始顺序构建嵌套结构
  const buildNestedStructure = (columns: any[]): any[] => {
    const result: any[] = [];
    let i = 0;

    while (i < columns.length) {
      const currentColumn = columns[i];
      const currentLevel = currentColumn.level;

      // 收集同一层级的连续列
      const sameLevelColumns = [];
      while (i < columns.length && columns[i].level === currentLevel) {
        sameLevelColumns.push(columns[i]);
        i++;
      }

      // 添加当前层级的所有列
      result.push(...sameLevelColumns);

      // 检查是否有下一层级的列
      if (i < columns.length && columns[i].level > currentLevel) {
        const nextLevel = columns[i].level;
        const nextLevelColumns = [];

        // 收集下一层级的所有列（包括更深层级）
        const startIndex = i;
        while (i < columns.length && columns[i].level >= nextLevel) {
          nextLevelColumns.push(columns[i]);
          i++;
        }

        // 创建 addGroup
        const nextLevelKey = levelKeys.find((k) => k.level === nextLevel);
        const addGroupKey = levelKeys
          .filter((k) => k.level < nextLevel)
          .map((k) => k.key);

        const addGroup: any = {
          addGroup: true,
          title: '',
          addGroupKey: addGroupKey,
          children: buildNestedStructure(nextLevelColumns),
        };

        // 如果不是 level 0，添加 addCellRender
        if (currentLevel >= 0) {
          addGroup.addCellRender = (r: any, i: number) => {
            const parentKeyName = levelKeys.find(
              (k) => k.level === currentLevel
            )?.key;
            const parentValue = parentKeyName ? r[parentKeyName] : undefined;
            const itemKey = nextLevelKey?.key;

            return (
              <div>
                <Button
                  type="text"
                  icon={<Plus />}
                  onClick={() => {
                    if (itemKey) {
                      tableRef.current.treeAddToParent(
                        itemKey,
                        itemKey === 'quoteKey'
                          ? createQuoteKeyTemplate(r.cooperateAccountId)
                          : {},
                        parentValue
                      );
                    }
                  }}
                >
                  {itemKey === 'quoteKey' ? '添加一行' : '添加分发平台'}
                </Button>
              </div>
            );
          };
        }

        // 在当前层级的列后面插入操作列（如果需要）
        if (currentLevel >= 0) {
          const nodeLevel = currentLevel + 1;
          const nodeKey = levelKeys.find((k) => k.level === nodeLevel)?.key;
          const parentKey =
            nodeLevel > 1
              ? levelKeys.find((k) => k.level === nodeLevel - 1)?.key
              : undefined;
          const mergeRowSpanKey = levelKeys
            .filter((k) => k.level <= nodeLevel)
            .map((k) => k.key);

          const operateColumn = {
            title: `操作`,
            width: 132,
            dataIndex: `$l${nodeLevel}_operate`,
            fixed: currentLevel === 0 ? 'right' : '',
            mergeRowSpanKey: mergeRowSpanKey,
            render: (v: any, row: any, index: number) => {
              let copyButton = (
                <Button
                  type="text"
                  onClick={() => {
                    if (nodeKey) {
                      const parentValue = parentKey
                        ? row[parentKey]
                        : row[levelKeys[0].key];
                      tableRef.current.treeCopyRowById(
                        nodeKey,
                        row[nodeKey],
                        parentValue
                      );
                    }
                  }}
                >
                  复制
                </Button>
              );
              if (nodeLevel === 1 && cooperateAccountList?.length > 1) {
                copyButton = (
                  <CopyToMoreCooperateAccount
                    cooperateObjList={cooperateAccountList}
                    onConfirm={(value) => {
                      value.forEach((n) => {
                        if(n === row.cooperateAccountId) {
                          tableRef.current.treeCopyRowById(
                            nodeKey,
                            row[nodeKey],
                            n
                          );
                        } else {
                          tableRef.current.treeAddToParent(
                            nodeKey,
                            {...row, ...createQuoteKeyTemplate(n)},
                            n,
                            -1
                          );
                        }
                      });
                    }}
                  >
                    <Button type="text">复制</Button>
                  </CopyToMoreCooperateAccount>
                );
              }
              return (
                <Space size={12}>
                  <Button
                    type="text"
                    onClick={() => {
                      if (nodeKey) {
                        const childIndex = row[`l${nodeLevel}$index`];
                        const parentValue = parentKey
                          ? row[parentKey]
                          : row[levelKeys[0].key];
                        tableRef.current.treeAddToParent(
                          nodeKey,
                          nodeKey === 'quoteKey' ? createQuoteKeyTemplate(row.cooperateAccountId) : {},
                          parentValue,
                          childIndex + 1
                        );
                      }
                    }}
                  >
                    插入
                  </Button>
                  {copyButton}
                  <Button
                    onClick={() => {
                      if (nodeKey) {
                        tableRef.current.treeDeleteRowById(row[nodeKey]);
                      }
                    }}
                    type="text"
                    danger
                  >
                    删除
                  </Button>
                </Space>
              );
            },
          };

          // 将操作列添加到 children 的末尾
          !readOnly && addGroup.children.push(operateColumn);

          const checkboxCol = renderCheckboxCol?.({ nodeLevel, currentLevel, mergeRowSpanKey, nodeKey })

          checkboxCol && addGroup.children.push(checkboxCol)
        }

        result.push(addGroup);
      }
    }
    return result;
  };

  
  return {
    columns: buildNestedStructure(columnsList),
    unEditableColumns,
  }
}

// 处理本地数据模型
export const localDataToServerData = (localData: any[]) => {
  // 过滤带$addRow的行，同时删除以$开头的属性
  const serverData = [];
  for (const index in localData) {
    const item = localData[index];
    if (item?.$addRow?.toString() === 'cooperateAccountId') {
      // 二级新增行必过滤
      continue;
    }
    const preItem = localData[Number(index) - 1];
    if (
      item?.$addRow?.includes('quoteKey') &&
      item?.['quoteKey'] === preItem?.['quoteKey']
    ) {
      // 三级新增行,如果第一行不是新增行,则过滤
      // 相当于不填分平台数据
      continue;
    }
    if (item?.$addRow?.includes('quoteKey')) {
      // 三级新增行, 过滤l2$开头的字段
      for (const key in item) {
        if (key.startsWith('l2$')) {
          delete item[key];
        }
      }
    }

    const serverItem = {};
    for (const key in item) {
      if (key.startsWith('$')) {
        continue;
      }
      serverItem[key] = item[key];
    }
    serverData.push(serverItem);
  }
  return serverData;
};
